import { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";

export function MultiSelectDropdown({ label, options = [], selected = [], onChange, groupBy, className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const selectAll = () => {
    const allVals = options.map(o => typeof o === "string" ? o : o.value);
    onChange(allVals);
  };

  const clear = () => onChange([]);

  // Group options by groupBy key if provided
  const grouped = {};
  if (groupBy) {
    options.forEach(o => {
      const g = typeof o === "string" ? "" : (o[groupBy] || "Other");
      if (!grouped[g]) grouped[g] = [];
      grouped[g].push(o);
    });
  }

  const count = selected.length;

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`h-8 px-3 text-xs rounded-sm border flex items-center gap-1.5 transition-colors whitespace-nowrap ${count > 0 ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}
        data-testid={`multiselect-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {label}
        {count > 0 && <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold bg-blue-600 text-white rounded-full">{count}</span>}
        <svg className={`w-3 h-3 ml-0.5 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-zinc-200 rounded-sm shadow-lg z-50 min-w-[220px] max-h-[320px] overflow-hidden flex flex-col" data-testid={`multiselect-panel-${label.toLowerCase().replace(/\s+/g, '-')}`}>
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-zinc-100 shrink-0">
            <label className="flex items-center gap-1.5 text-[10px] text-zinc-500 cursor-pointer">
              <input type="checkbox" checked={count === options.length && count > 0} onChange={() => count === options.length ? clear() : selectAll()} className="w-3 h-3 accent-blue-600" />
              Select all
            </label>
            {count > 0 && <button onClick={clear} className="text-[10px] text-red-500 hover:underline">Clear</button>}
          </div>
          <div className="overflow-y-auto flex-1">
            {groupBy && Object.keys(grouped).length > 0 ? (
              Object.entries(grouped).map(([group, items]) => (
                <div key={group}>
                  {group && <div className="px-3 py-1 text-[9px] font-semibold uppercase text-zinc-400 bg-zinc-50 sticky top-0">{group}</div>}
                  {items.map(o => {
                    const val = typeof o === "string" ? o : o.value;
                    const lbl = typeof o === "string" ? o : o.label;
                    return (
                      <label key={val} className="flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-50 cursor-pointer text-xs text-zinc-700">
                        <input type="checkbox" checked={selected.includes(val)} onChange={() => toggle(val)} className="w-3 h-3 accent-blue-600 shrink-0" />
                        <span className="truncate">{lbl}</span>
                      </label>
                    );
                  })}
                </div>
              ))
            ) : (
              options.map(o => {
                const val = typeof o === "string" ? o : o.value;
                const lbl = typeof o === "string" ? o : o.label;
                return (
                  <label key={val} className="flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-50 cursor-pointer text-xs text-zinc-700">
                    <input type="checkbox" checked={selected.includes(val)} onChange={() => toggle(val)} className="w-3 h-3 accent-blue-600 shrink-0" />
                    <span className="truncate">{lbl}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function SelectedTags({ selected = [], options = [], onRemove }) {
  if (selected.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {selected.map(val => {
        const opt = options.find(o => (typeof o === "string" ? o : o.value) === val);
        const lbl = opt ? (typeof opt === "string" ? opt : opt.label) : val;
        return (
          <span key={val} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] bg-blue-100 text-blue-700 rounded-sm">
            {lbl}
            <button onClick={() => onRemove(val)} className="hover:text-blue-900"><X size={8} /></button>
          </span>
        );
      })}
    </div>
  );
}
